import { useEffect, useRef, useState } from "react"
import iwiion from "./iwiion.png"
import iwiioff from "./iwiioff.png"
import PrinterDialog from "./printerdialog"
import { ImageWriterII, registerSetPrinting } from "./iwii"
import { constructAudio, playAudio } from "../../../common/utility"
import { isAudioEnabled, registerAudioContext } from "../audio/speaker"
import { handleGetSlotConfig, setSerialConfigCallback } from "../../main2worker"
import { getSerialMode } from "../serial/serialhub"
import { useTranslation } from "../../../i18n/useTranslation"
import type { RetroControlMetadata } from "../../retro/retromenucontext"
import { controlsFromJson, type RetroControlBindings } from "../../retro/retrocontrolmetadata"

const OPEN_IMAGEWRITER_EVENT = "apple2ts:open-imagewriter"

export const openImageWriterDialog = () => window.dispatchEvent(new Event(OPEN_IMAGEWRITER_EVENT))

const imageWriterBindings: RetroControlBindings = {
  "printer.imageWriterII": {
    selectable: () => handleGetSlotConfig()[1] !== "none",
    action: openImageWriterDialog,
  },
}

export const retroImageWriterControls: RetroControlMetadata[] = controlsFromJson("imagewriter", imageWriterBindings)

enum AUDIO {
  TURN_ON = 0,
  PRINT = 1,
  LINEFEED = 2,
}
const PRINTER_DURATIONS = [0.7083, 1.292, 2.459, 3.625, 4.125, 5.375, 6.5, 7.625, 8.792, 9.75] // seconds
const CLIP_LENGTH = PRINTER_DURATIONS[PRINTER_DURATIONS.length - 1]

const ImageWriter = ({ showLauncher = true }: { showLauncher?: boolean }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [canvas] = useState(document.createElement("canvas"))
  const [isPrinting, setIsPrinting] = useState(false)
  const printingTimeoutRef = useRef<number>(0)
  const audioDevicesRef = useRef<Array<AudioDevice | undefined>>([])
  const audioStartTimeRef = useRef<number>(0)
  const setPrintCallsRef = useRef<number>(0)

  const slotConfig = handleGetSlotConfig()
  const isPrinterDisabled = slotConfig[1] === "none"

  const playPrinterAudio = (audio: AUDIO, audioFile: string, duration: number, loop: boolean) => {
    if (!isAudioEnabled()) {
      audioDevicesRef.current[audio]?.context.close()
      audioDevicesRef.current[audio] = undefined
    }
    if (!audioDevicesRef.current[audio]) {
      audioDevicesRef.current[audio] = constructAudio(audioFile)
      if (loop) {
        audioDevicesRef.current[audio]!.element.loop = true
      }
      registerAudioContext((enable: boolean) => {
        if (!enable) {
          audioDevicesRef.current[audio]?.context.close()
          audioDevicesRef.current[audio] = undefined
        }
      })
    }
    playAudio(audioDevicesRef.current[audio], duration)
  }

  useEffect(() => {
    ImageWriterII.startup(canvas)
    setSerialConfigCallback((config: SerialConfig) => {
      if (config.baud > 0 && getSerialMode() === 0) {
        playPrinterAudio(AUDIO.TURN_ON, window.assetRegistry.imagewriterTurnOn, 3000, false)
        audioDevicesRef.current[AUDIO.TURN_ON]!.element.addEventListener("ended", () => {
          if (audioDevicesRef.current[AUDIO.TURN_ON]?.context.state !== "closed") {
            audioDevicesRef.current[AUDIO.TURN_ON]?.context.close()
          }
          audioDevicesRef.current[AUDIO.TURN_ON] = undefined
        })
      }
    })

    // Cleanup audio on unmount
    const audioDevices = audioDevicesRef.current
    return () => {
      for (const audio in audioDevices) {
        audioDevices[audio]?.context.close()
        audioDevices[audio] = undefined
      }
    }
  }, [canvas])

  const setPrinting = () => {
    if (audioDevicesRef.current[AUDIO.TURN_ON]) {
      return
    }
    // Start audio on first call or if it was previously stopped
    if (printingTimeoutRef.current === 0) {
      // Always restart audio and reset timer for a new print job
      if (audioDevicesRef.current[AUDIO.PRINT]) {
        audioDevicesRef.current[AUDIO.PRINT]?.context.close()
        audioDevicesRef.current[AUDIO.PRINT] = undefined
      }
      playPrinterAudio(AUDIO.PRINT, window.assetRegistry.imagewriterPrint, 975000, true)
      audioStartTimeRef.current = Date.now()
    } else {
      // Just reset the timeout if already printing
      clearTimeout(printingTimeoutRef.current)
    }

    setPrintCallsRef.current++
    const overallPrintTime = (Date.now() - audioStartTimeRef.current) / 1000
    const remainingTimeout = (setPrintCallsRef.current > 10 && overallPrintTime <= CLIP_LENGTH) ? 2000 : 500

    const timeout = window.setTimeout(() => {
      // Find the correct slot within PRINTER_DURATIONS based on elapsed time to ensure a smooth stop
      const overallPrintTime = (Date.now() - audioStartTimeRef.current) / 1000
      const timeWithinAudioClip = overallPrintTime % CLIP_LENGTH
      let targetDuration = CLIP_LENGTH // default to last
      for (const duration of PRINTER_DURATIONS) {
        if (duration > timeWithinAudioClip) {
          targetDuration = duration
          break
        }
      }
      // Timeout to reach target duration
      const timeoutMs = (targetDuration - timeWithinAudioClip) * 1000
      window.setTimeout(() => {
        printingTimeoutRef.current = 0
        setIsPrinting(false)
        audioStartTimeRef.current = 0
        setPrintCallsRef.current = 0
        // Stop printer sound
        if (audioDevicesRef.current[AUDIO.PRINT]) {
          audioDevicesRef.current[AUDIO.PRINT]?.context.close()
          audioDevicesRef.current[AUDIO.PRINT] = undefined
        }
        if (overallPrintTime > 1) {
          playPrinterAudio(AUDIO.LINEFEED, window.assetRegistry.imagewriterLinefeed, 2000, false)
        }
      }, timeoutMs)
    }, remainingTimeout)
    printingTimeoutRef.current = timeout
    setIsPrinting(true)
  }

  useEffect(() => {
    registerSetPrinting(setPrinting)
  })

  useEffect(() => {
    if (showLauncher) return
    const openDialog = () => setOpen(true)
    window.addEventListener(OPEN_IMAGEWRITER_EVENT, openDialog)
    return () => window.removeEventListener(OPEN_IMAGEWRITER_EVENT, openDialog)
  }, [showLauncher])

  const img1 = isPrinting ? iwiion : iwiioff
  const isTouchDevice = "ontouchstart" in document.documentElement

  return <>
    {showLauncher && <span className="flex-column" style={{ opacity: isPrinterDisabled ? 0.4 : 1, filter: isPrinterDisabled ? "grayscale(100%)" : "none", pointerEvents: isPrinterDisabled ? "none" : "auto" }}>
      <img className={`disk-image${isTouchDevice ? " disk-image-small" : ""}`}
        style={{ borderWidth: 0, cursor: isPrinterDisabled ? "not-allowed" : "pointer" }}
        src={img1} alt="iwii"
        title={t("print.imageWriterII")}
        height="57px"
        onClick={() => { if (!isPrinterDisabled) setOpen(true) }} />
    </span>}
    <PrinterDialog
      open={open}
      onClose={() => { setOpen(false) }}
      canvas={canvas}
      printer={ImageWriterII}
    />
  </>
}

export default ImageWriter
